/**
 * Safely extracts the access point ID from a scanned Smart Sign QR code URL.
 *
 * Accepts a URL only if its origin is in the allow-list, then extracts the ID
 * by locating the `entry` path segment. This handles both supported shapes:
 *   - /entry/{id}        (Chirp URLs)
 *   - /chirp/entry/{id}  (STRATIS URLs)
 *
 * Returns null for any input that is not a URL, has an unrecognized origin,
 * or does not contain a valid ID following an `entry` segment.
 */
export function getScannedAccessPointId(
  scannedUrl: string,
  allowedOrigins: string[],
): string | null {
  let parsedUrl: URL;

  try {
    parsedUrl = new URL(scannedUrl);
  } catch {
    return null; // Not a URL
  }

  const { origin, pathname } = parsedUrl;

  if (!allowedOrigins.includes(origin)) {
    return null; // Origin is not in allow-list
  }

  const segments = pathname.split('/').filter(Boolean);
  const entryIndex = segments.indexOf('entry');

  if (entryIndex === -1) {
    return null; // No /entry/ path segment
  }

  return segments[entryIndex + 1] || null;
}
